import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdelantoForm } from '../../components/AdelantoForm.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { FwaApiError } from '../../lib/api.js';
import { useAdelanto, useUpdateAdelanto } from '../../lib/queries/adelantos.js';

export function AdelantosEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useAdelanto(id);
  const update = useUpdateAdelanto();
  const [serverError, setServerError] = useState<string>('');
  const [showConflictModal, setShowConflictModal] = useState(false);

  if (isLoading) return <div className="text-center py-8 text-gray-600">Cargando…</div>;
  if (!data || !id) {
    return (
      <Card>
        <CardBody>
          <p className="text-gray-600">Adelanto no encontrado.</p>
          <Link to="/adelantos">
            <Button variant="ghost" className="mt-4">
              ← Regresar al listado
            </Button>
          </Link>
        </CardBody>
      </Card>
    );
  }

  const adelanto = data.data;
  const etag = data.etag ?? adelanto.version_id;

  if (adelanto.estatus === 'revocado') {
    return (
      <Card>
        <CardBody>
          <p className="text-gray-600">
            Este adelanto está revocado (estado terminal). No puede editarse.
          </p>
          <Link to={`/adelantos/${id}`}>
            <Button variant="ghost" className="mt-4">
              ← Regresar al detalle
            </Button>
          </Link>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900">Editar adelanto</h1>
      <p className="text-sm text-gray-500">
        Versión actual: <span className="font-mono text-xs">{adelanto.version_id}</span>
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Datos editables</CardTitle>
        </CardHeader>
        <CardBody>
          <AdelantoForm
            mode="edit"
            initialValues={{
              tipo_adelanto: adelanto.tipo_adelanto,
              nombre_adelanto: adelanto.nombre_adelanto,
              comentario: adelanto.comentario ?? undefined,
            }}
            onSubmit={(payload) => {
              setServerError('');
              update.mutate(
                { id, payload, etag },
                {
                  onSuccess: (res) => {
                    if ('queuedOffline' in res) {
                      navigate('/outbox');
                      return;
                    }
                    navigate(`/adelantos/${id}`);
                  },
                  onError: (err) => {
                    if (err instanceof FwaApiError) {
                      if (err.status === 412) {
                        setShowConflictModal(true);
                        return;
                      }
                      setServerError(err.problem.detail ?? err.problem.title);
                    } else {
                      setServerError(err.message);
                    }
                  },
                },
              );
            }}
            isSubmitting={update.isPending}
            serverError={serverError}
          />
        </CardBody>
      </Card>

      {showConflictModal && (
        <dialog
          open
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
          aria-labelledby="conflict-title-ade"
        >
          <Card className="max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                <span id="conflict-title-ade">Conflicto de versión</span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              <p>
                Otra persona editó este adelanto mientras tú estabas viendo. Recarga para ver los
                cambios más recientes y vuelve a aplicar tus cambios.
              </p>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowConflictModal(false);
                    void refetch();
                  }}
                >
                  Recargar y re-intentar
                </Button>
                <Button variant="ghost" onClick={() => navigate(`/adelantos/${id}`)}>
                  Descartar cambios
                </Button>
              </div>
            </CardBody>
          </Card>
        </dialog>
      )}

      <Link to={`/adelantos/${id}`}>
        <Button variant="ghost">← Cancelar edición</Button>
      </Link>
    </div>
  );
}
